import { Countries } from './api.service';
import englishCountries from '../../assets/json/countries/en.json';

/**
 * DEV-7522: the country list used when assets/json/countries/<lang>.json fails to load.
 * Both country dropdowns are filled from that one request, so a timeout used to leave
 * them empty - and movingTo's country is required, which stopped the visitor there.
 *
 * Only the English list is bundled (one file instead of fifteen). The names are
 * translated by the browser with Intl.DisplayNames, which needs no network; a browser
 * without it gets the English names, which still lets the visitor continue.
 *
 * Intl.DisplayNames only knows real region codes, so the custom ones in our lists
 * (England, Holland, ...) take their names from CUSTOM_REGION_NAMES instead. A code
 * that a language's own list leaves out is left out here too.
 */
export function fallbackCountries( locale: string, source: Countries = englishCountries ): Countries {

	const language = locale.split( '-' )[0];

	// The bundled list is the English list itself, so English keeps its own names
	const displayNames = language === 'en' ? null : regionNames( locale );
	const countries: Countries = [];

	for( const c of source ) {

		const custom = CUSTOM_REGION_NAMES[c.country_code];

		if( custom && !custom[language] ) {

			continue;
		}

		countries.push( {
			country_code: c.country_code,
			country: custom?.[language] || displayNames?.( c.country_code ) || c.country,
		} );
	}

	return countries.sort( ( a, b ) => a.country.localeCompare( b.country, locale ) );
}

/**
 * Names of the non-ISO codes in assets/json/countries/<lang>.json, per language.
 * Copied from those files; country-fallback.spec.ts fails when they drift apart.
 */
export const CUSTOM_REGION_NAMES: Record<string, Record<string, string>> = {
	US1: { // America
		da: 'Amerika',
		de: 'Amerika',
		el: 'Αμερική',
		en: 'America',
		es: 'América',
		fr: 'Amérique',
		it: 'America',
		nl: 'Amerika',
		pl: 'Ameryka',
		pt: 'América',
		ro: 'America',
		sv: 'Amerika',
		tr: 'Amerika',
	},
	GB1: { // England
		da: 'England',
		de: 'England',
		el: 'Αγγλία',
		en: 'England',
		es: 'Inglaterra',
		fr: 'Angleterre',
		it: 'Inghilterra',
		nl: 'Engeland',
		pl: 'Anglia',
		pt: 'Inglaterra',
		ro: 'Anglia',
		sv: 'England',
		tr: 'İngiltere',
	},
	GB2: { // Scotland
		da: 'Skotland',
		de: 'Schottland',
		el: 'Σκωτία',
		en: 'Scotland',
		es: 'Escocia',
		fr: 'Écosse',
		it: 'Scozia',
		nl: 'Schotland',
		pl: 'Szkocja',
		pt: 'Escócia',
		ro: 'Scoția',
		sv: 'Skottland',
		tr: 'İskoçya',
	},
	GB3: { // Wales
		da: 'Wales',
		de: 'Wales',
		el: 'Ουαλία',
		en: 'Wales',
		es: 'Gales',
		fr: 'Pays de Galles',
		it: 'Galles',
		nl: 'Wales',
		pl: 'Walia',
		pt: 'País de Gales',
		ro: 'Țara Galilor',
		sv: 'Wales',
		tr: 'Galler',
	},
	NL1: { // Holland
		da: 'Holland',
		de: 'Holland',
		el: 'Ολλανδία',
		en: 'Holland',
		es: 'Holanda',
		fr: 'Hollande',
		it: 'Olanda',
		ms: 'Holland',
		nl: 'Holland',
		pl: 'Holandia',
		pt: 'Holanda',
		ro: 'Olanda',
		sv: 'Holland (Nederländerna)',
		th: 'ฮอลแลนด์',
		tr: 'Hollanda',
	},
	CW1: { // Netherlands Antilles
		da: 'Nederlandske Antiller',
		de: 'Niederländische Antillen',
		el: 'Ολλανδικές Αντίλλες',
		en: 'Netherlands Antilles',
		es: 'Antillas Neerlandesas',
		fr: 'Antilles néerlandaises',
		it: 'Antille Olandesi',
		nl: 'Nederlandse Antillen',
		pl: 'Antyle Holenderskie',
		pt: 'Antilhas Neerlandesas',
		ro: 'Antilele Olandeze',
		sv: 'Nederländska Antillerna',
		tr: 'Hollanda Antilleri',
	},
};

function regionNames( locale: string ): ( ( code: string ) => string | undefined ) | null {

	const DisplayNames = ( Intl as any ).DisplayNames;

	if( typeof DisplayNames !== 'function' ) {

		return null;
	}

	try {

		const names = new DisplayNames( [ locale ], { type: 'region', fallback: 'none' } );

		// of() throws a RangeError on a code it does not accept as a region
		return code => {

			try {

				return names.of( code );
			}
			catch {

				return undefined;
			}
		};
	}
	catch {

		return null;
	}
}
